import { useCallback, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { QK } from "../queryKeys";
import { useAgentsHttp } from "../useAgentsHttp";
import {
  type AsyncOperationProgress,
  isOperationFinished,
  useAsyncOperation,
} from "./useAsyncOperation";
import { invalidateOperationsDashboard } from "./useOperationsDashboard";

/** What the worker records once every linked site has been visited. Must agree with
 * ``run_refresh_all_sites_worker`` in ``bma_site_refresh.py``. */
export interface RefreshAllSitesResult {
  total: number;
  refreshed: number;
  failed: string[];
  not_found: string[];
  unreached: string[];
}

// Starts the background refresh (a click while one runs joins it), picks a running one
// back up when the page opens mid-run, and on completion refetches everything that
// shows platform data.
export const useRefreshAllSites = () => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  const [operationKey, setOperationKey] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [result, setResult] = useState<RefreshAllSitesResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const active = useQuery(
    [QK.refreshAllSitesActive],
    () =>
      http<{ operation_key: string | null }>(
        "/projects/refresh-all-sites/active",
        {},
      ),
    { staleTime: 0, cacheTime: 0, refetchOnWindowFocus: false },
  );
  useEffect(() => {
    const key = active.data?.operation_key;
    if (key) setOperationKey((current) => current ?? key);
  }, [active.data]);

  const op = useAsyncOperation(operationKey, {
    enabled: operationKey !== null,
  });

  useEffect(() => {
    if (!op.data) return;
    const { status } = op.data;
    if (!isOperationFinished(status)) return;
    if (status === "completed") {
      setResult(op.data.triage as unknown as RefreshAllSitesResult);
      void qc.invalidateQueries({ queryKey: [QK.projectsList] });
      void qc.invalidateQueries({ queryKey: [QK.projectGet] });
      void qc.invalidateQueries({ queryKey: [QK.dashboard] });
      invalidateOperationsDashboard(qc);
    } else {
      setError(op.data.error ?? "The refresh stopped before it finished.");
    }
    setOperationKey(null);
  }, [op.data, qc]);

  const start = useCallback(async () => {
    setError(null);
    setResult(null);
    setStarting(true);
    try {
      const { operation_key } = await http<{ operation_key: string }>(
        "/projects/refresh-all-sites",
        {},
      );
      setOperationKey(operation_key);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setStarting(false);
    }
  }, [http]);

  const dismiss = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  const progress: AsyncOperationProgress | null =
    op.data?.progress ??
    (operationKey || starting
      ? { done: 0, total: null, label: "Starting" }
      : null);

  return {
    start,
    running: starting || operationKey !== null,
    progress,
    result,
    error,
    dismiss,
  };
};
